/**
 * Modello puro delle pagine ticket del richiedente associato
 * (`/area-riservata/supporto` e `/area-riservata/supporto/:id`).
 *
 * Qui non entra nessun JSX e nessuna chiamata di rete: sono le sole decisioni
 * che il richiedente vede (a che punto e la pratica, se tocca a lui, come si
 * ordina e si filtra la lista) e devono essere verificabili con un test.
 *
 * I dati sono quelli di `toRequesterView` (backend/utils/supportTicket.js):
 * niente SLA, niente note interne, niente assegnatari; dei trasferimenti resta
 * solo la data.
 */

/** Stati in cui la pratica e conclusa: niente piu comunicazioni. */
export const CONCLUDED_STATUSES = ['risolto', 'chiuso'];

/**
 * Avanzamento degli stati sulla linea del tempo. `in_attesa_associato` ha lo
 * stesso rango di `in_lavorazione`: e la lavorazione ferma in attesa di noi,
 * non una tappa successiva.
 */
const STATUS_RANK: Record<string, number> = {
  aperto: 0,
  preso_in_carico: 1,
  in_lavorazione: 2,
  in_attesa_associato: 2,
  risolto: 3,
  chiuso: 4,
};

const rankOf = (status?: string): number =>
  typeof status === 'string' && status in STATUS_RANK ? STATUS_RANK[status] : 0;

/** Forma minima letta dalla timeline: e un sottoinsieme di `RequesterTicketDetail`. */
export interface RequesterTimelineTicket {
  status: string;
  /** Pratica unita a un'altra (vista richiedente): l'ultima tappa e «Unita». */
  merged?: boolean;
  createdAt?: string;
  firstResponseAt?: string;
  resolvedAt?: string;
  closedAt?: string;
  transfers?: { at: string }[];
}

export type TimelineStepState = 'done' | 'current' | 'todo';

export interface RequesterTimelineStep {
  /** Chiave stabile per il `key` di React e per i test. */
  key: string;
  label: string;
  /** Data ISO della tappa, quando il backend la espone. */
  at?: string;
  state: TimelineStepState;
  /** `attesa` evidenzia la tappa in cui la palla e nel campo del richiedente. */
  tone: 'neutral' | 'attesa';
}

/** Forma minima letta dalla lista e dagli avvisi. */
export interface RequesterActionTicket {
  status: string;
  unread?: { associate?: boolean };
}

/**
 * Vero quando la pratica aspetta qualcosa dal richiedente: o il canale ha
 * chiesto esplicitamente (stato `in_attesa_associato`), o c'e una risposta che
 * lui non ha ancora letto.
 */
export const needsRequesterAction = (ticket: RequesterActionTicket | null | undefined): boolean =>
  !!ticket && (ticket.status === 'in_attesa_associato' || !!ticket.unread?.associate);

/** Vero quando la pratica e chiusa o risolta. */
export const isConcluded = (ticket: { status: string } | null | undefined): boolean =>
  !!ticket && CONCLUDED_STATUSES.includes(ticket.status);

/**
 * Costruisce le tappe della pratica. Le tappe raggiunte portano la data quando
 * il backend la espone (l'avanzamento a "In lavorazione" non ha una data
 * propria: resta una tappa senza data, non si inventa `lastActivityAt`).
 *
 * L'ultima tappa raggiunta e `current`, quelle prima `done`, quelle dopo
 * `todo`. I trasferimenti compaiono dopo la presa in carico, uno per ogni voce
 * di `transfers[]`, con la sola data che il richiedente puo vedere.
 */
export const buildRequesterTimeline = (
  ticket: RequesterTimelineTicket | null | undefined
): RequesterTimelineStep[] => {
  if (!ticket) return [];
  const rank = rankOf(ticket.status);

  const raw: { key: string; label: string; at?: string; done: boolean; tone?: 'attesa' }[] = [
    { key: 'aperta', label: 'Aperta', at: ticket.createdAt, done: true },
    {
      key: 'presa',
      label: 'Presa in carico',
      at: ticket.firstResponseAt,
      done: !!ticket.firstResponseAt || rank >= 1,
    },
  ];

  (ticket.transfers || []).forEach((t, i) => {
    raw.push({ key: `trasferimento-${i}`, label: 'Passata a un altro gruppo', at: t.at, done: true });
  });

  raw.push({ key: 'lavorazione', label: 'In lavorazione', done: rank >= 2 });

  if (ticket.status === 'in_attesa_associato') {
    raw.push({
      key: 'attesa',
      label: 'In attesa di una tua risposta',
      done: true,
      tone: 'attesa',
    });
  }

  raw.push({
    key: 'risolta',
    label: 'Risolta',
    at: ticket.resolvedAt,
    done: !!ticket.resolvedAt || rank >= 3,
  });
  raw.push({
    key: 'chiusa',
    // Tappe al femminile («la pratica»): «Unita» per una pratica unita a un'altra.
    label: ticket.merged ? 'Unita' : 'Chiusa',
    at: ticket.closedAt,
    done: !!ticket.closedAt || rank >= 4,
  });

  let lastDone = -1;
  raw.forEach((s, i) => {
    if (s.done) lastDone = i;
  });

  return raw.map((s, i) => ({
    key: s.key,
    label: s.label,
    at: s.at,
    state: s.done ? (i === lastDone ? 'current' : 'done') : 'todo',
    tone: s.tone === 'attesa' ? 'attesa' : 'neutral',
  }));
};

/**
 * Finestra di riapertura, speculare a `canReopen` di
 * `backend/utils/supportTicket.js`: SOLO da 'risolto' (dichiarazione
 * dell'operatore) ed entro REOPEN_WINDOW_DAYS da `resolvedAt`. Uno stato
 * 'chiuso' e definitivo: per un nuovo problema si apre una nuova richiesta.
 * Il server resta l'arbitro: quando la finestra scade fra il render e il click
 * risponde 409 e la pagina mostra il toast.
 */
export const canReopenRequester = (
  ticket: { status: string; closedAt?: string; resolvedAt?: string } | null | undefined,
  windowDays: number,
  now: Date = new Date()
): boolean => {
  if (!ticket || ticket.status !== 'risolto') return false;
  if (!ticket.resolvedAt) return false;
  const ms = new Date(ticket.resolvedAt).getTime();
  if (Number.isNaN(ms)) return false;
  return now.getTime() - ms <= windowDays * 24 * 60 * 60 * 1000;
};

/**
 * Scadenza della finestra di riapertura (per la riga "puoi riaprirla fino al ...").
 * Null se il ticket non e in stato 'risolto' o non ha `resolvedAt`.
 */
export const reopenDeadline = (
  ticket: { status: string; resolvedAt?: string } | null | undefined,
  windowDays: number
): Date | null => {
  if (!ticket || ticket.status !== 'risolto' || !ticket.resolvedAt) return null;
  const ms = new Date(ticket.resolvedAt).getTime();
  if (Number.isNaN(ms)) return null;
  return new Date(ms + windowDays * 24 * 60 * 60 * 1000);
};

/** Voce del thread pubblico, ridotta a cio che serve al modello. */
export interface RequesterThreadMessage {
  authorType: string;
  sentAt?: string;
}

/**
 * Vero quando l'ultimo messaggio del thread e del richiedente: e la condizione
 * dell'avviso "Hai gia inviato una comunicazione". Il confronto e sul tipo di
 * autore, non sulla posizione: un thread vuoto non e "mio".
 */
export const lastMessageIsMine = (
  thread: RequesterThreadMessage[] | null | undefined,
  mineAuthorType: string = 'associate'
): boolean => {
  if (!thread || thread.length === 0) return false;
  return thread[thread.length - 1].authorType === mineAuthorType;
};

// ---------------------------------------------------------------------------
// Lista
// ---------------------------------------------------------------------------

/** Forma minima di una riga della lista. */
export interface RequesterListTicket {
  _id: string;
  ticketNumber: string;
  subject: string;
  status: string;
  lastActivityAt?: string;
  unread?: { associate?: boolean };
}

export type RequesterListTab = 'in_corso' | 'concluse' | 'tutte';

export interface RequesterCounters {
  inCorso: number;
  daLeggere: number;
  concluse: number;
}

/**
 * I tre contatori dell'intestazione. "Da leggere" conta le pratiche in cui
 * tocca al richiedente (risposta non letta o attesa esplicita), comprese
 * quelle gia concluse: una risposta non letta va letta comunque.
 */
export const countRequesterTickets = (items: RequesterListTicket[]): RequesterCounters => {
  const list = items || [];
  return {
    inCorso: list.filter((t) => !isConcluded(t)).length,
    daLeggere: list.filter((t) => needsRequesterAction(t)).length,
    concluse: list.filter((t) => isConcluded(t)).length,
  };
};

/** Normalizza la ricerca: minuscole, senza spazi ai bordi e senza il `#` del numero. */
const normalizeSearch = (search: string): string =>
  (search || '').trim().toLowerCase().replace(/^#/, '');

/** Filtra per pillola di stato e per testo (numero pratica oppure oggetto). */
export const filterRequesterTickets = <T extends RequesterListTicket>(
  items: T[],
  tab: RequesterListTab,
  search: string
): T[] => {
  const q = normalizeSearch(search);
  return (items || []).filter((t) => {
    if (tab === 'in_corso' && isConcluded(t)) return false;
    if (tab === 'concluse' && !isConcluded(t)) return false;
    if (!q) return true;
    const num = (t.ticketNumber || '').toLowerCase();
    const subj = (t.subject || '').toLowerCase();
    return num.includes(q) || subj.includes(q);
  });
};

/**
 * Ordina la lista: prima le pratiche in cui tocca al richiedente, poi per
 * ultima attivita decrescente (lo stesso ordine che gia arriva dal backend).
 * Non muta l'array in ingresso.
 */
export const sortRequesterTickets = <T extends RequesterListTicket>(items: T[]): T[] => {
  const timeOf = (t: T) => {
    const ms = t.lastActivityAt ? new Date(t.lastActivityAt).getTime() : NaN;
    return Number.isNaN(ms) ? 0 : ms;
  };
  return [...(items || [])].sort((a, b) => {
    const aAction = needsRequesterAction(a);
    const bAction = needsRequesterAction(b);
    if (aAction !== bAction) return aAction ? -1 : 1;
    return timeOf(b) - timeOf(a);
  });
};

/** Taglia la pagina corrente (paginazione lato client, 10 per pagina). */
export const paginate = <T>(items: T[], page: number, perPage: number): T[] => {
  const list = items || [];
  const start = Math.max(0, (page - 1) * perPage);
  return list.slice(start, start + perPage);
};
